/* eslint-disable react/no-unescaped-entities */
import React from "react";
import PropTypes from "prop-types";
import { formatCommaZeroDp, formatPerc } from "../utils/dataHelpers/formatters";

const dayPlural = (number) => {
  return Math.round(number) === 1 ? "day" : "days";
};

export const H = ({ children }) => {
  /**
   * Adds a className to a text fragment
   */
  return <span className="text__emphasis">{children}</span>;
};

H.propTypes = { children: PropTypes.node.isRequired };

// AVERAGE BLURBS
export const BlurbPtoAvg = ({ countyName, avgPtoDays, year }) => {
  const positiveAvg = (
    <span>
      Our analysis shows judges in {countyName} County had no court proceedings or judicial training on an average of <H>{formatCommaZeroDp(avgPtoDays)}</H> business {dayPlural(avgPtoDays)} in {year}. We calculated this by subtracting <H>122</H> from
      the number of days they had no case events that year. <H>122</H> is the
      number of days a judge would have no case events if they didn&rsquo;t work
      any weekend days (<H>104</H>
      ), holidays (<H>13</H>), or judicial training days (<H>5</H>
      ).
    </span>
  );
  const negativeAvg = (
    <span>
      Our analysis shows judges in {countyName} County handled cases on an average of{" "}
      <H>{formatCommaZeroDp(avgPtoDays * -1)}</H> extra days in {year} they normally
      would have not been expected to have proceedings (like holidays or weekends, or comp time in exchange
      for working those days). However, keep in mind
      that just because a judge has case events on a given day it doesn't
      necessarily mean they worked an eight-hour day.
    </span>
  );

  const finalBlurb = avgPtoDays > 0 ? positiveAvg : negativeAvg;
  return <div className="text__container">{finalBlurb}</div>;
};

BlurbPtoAvg.propTypes = {
  countyName: PropTypes.string.isRequired,
  avgPtoDays: PropTypes.number.isRequired,
  year: PropTypes.number.isRequired,
};

export const BlurbTimeAvg = ({ countyName, avgNoAfternoonDays, year }) => (
  <div className="text__container">
    On average in {year}, we estimate judges in {countyName} County had no case
    events that began after 1 p.m. on{" "}
    <H>{formatCommaZeroDp(avgNoAfternoonDays)}</H> days during which they presided over court proceedings.
  </div>
);
BlurbTimeAvg.propTypes = {
  countyName: PropTypes.string.isRequired,
  avgNoAfternoonDays: PropTypes.number.isRequired,
  year: PropTypes.number.isRequired,
};

// INDIVIDUAL JUDGE BLURBS

export const BlurbPto = ({ lastName, paidTimeOff = 5, year = 2019 }) => {
  return (
    <div className="text__container">
      Our analysis shows Judge {lastName} had no case events on <H>{paidTimeOff}</H>{" "}
      {dayPlural(paidTimeOff)} in <H>{year}</H>. We calculated
      that by subtracting <H>122</H> (the annual number of weekend, holiday and judicial training days) from the total number of days the judge had no case events.
    </div>
  );
};

BlurbPto.propTypes = {
  lastName: PropTypes.string.isRequired,
  paidTimeOff: PropTypes.number.isRequired,
  year: PropTypes.number.isRequired,
};

export const BlurbExtraWork = ({ lastName, extraWorkDays, year }) => {
  return (
    <div className="text__container">
      We estimate Judge {lastName} handled case events on{" "}
      {extraWorkDays === 1 ? (
        "an extra day"
      ) : (
        <span>
          <H>{extraWorkDays}</H> extra days
        </span>
      )}{" "}
      in {year} they normally
      would have not been expected to have proceedings (such as weekends,
      holidays or comp time for having worked on of those days). We calculated
      that by subtracting <H>122</H> (104 weekend days, 13 holidays and 5
      judicial training days) from the number of days {lastName} had no case
      events.
    </div>
  );
};

BlurbExtraWork.propTypes = {
  lastName: PropTypes.string.isRequired,
  extraWorkDays: PropTypes.number.isRequired,
  year: PropTypes.number.isRequired,
};

export const BlurbTime = ({
  lastName,
  totalDaysWithEvents,
  daysWithOnlyEventsBefore1Pm,
  percDaysWithOnlyEventsBefore1Pm,
  year = 2000,
}) => {
  return (
    <div className="text__container">
      We estimate Judge {lastName} had no case events that began after 1 p.m. on{" "}
      <H>{daysWithOnlyEventsBefore1Pm}</H> of the <H>{totalDaysWithEvents}</H>{" "}
      days the judge handled case events in {year}. Note that this is a
      conservative estimate because the courts don't record time data for a
      small number of case events. In order to be as fair as possible, we
      excluded from our calculation any days where complete time data was
      unavailable. This means {lastName} may have had more days with no case
      events after 1 p.m. than we were able to calculate.
    </div>
  );
};

BlurbTime.propTypes = {
  lastName: PropTypes.string.isRequired,
  totalDaysWithEvents: PropTypes.number.isRequired,
  daysWithOnlyEventsBefore1Pm: PropTypes.number.isRequired,
  percDaysWithOnlyEventsBefore1Pm: PropTypes.number.isRequired,
  year: PropTypes.number.isRequired,
};
